import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { RecordInfoButton } from '../components/RecordInfoButton';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import type { StaffRole } from '../types/models';

const emptyForm = { code: '', name: '' };

export function StaffRolePage() {
  const toast = useToast();
  const { orgId = '' } = useParams();

  const [roles, setRoles] = useState<StaffRole[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  async function refresh() {
    try {
      setRoles(await api.listStaffRoles(orgId));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load staff roles.');
    }
  }

  const filtered = useMemo(() => {
    if (!roles) return null;
    const q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter(r => r.code.toLowerCase().includes(q) || r.name.toLowerCase().includes(q));
  }, [roles, search]);

  function openCreate() {
    setFormError(null); setEditId(null); setForm({ ...emptyForm }); setShowModal(true);
  }

  function openEdit(r: StaffRole) {
    setFormError(null); setEditId(r.id); setForm({ code: r.code, name: r.name }); setShowModal(true);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setBusy(true);
    try {
      if (editId) await api.updateStaffRole(editId, form.code.trim(), form.name.trim());
      else await api.createStaffRole({ organizationId: orgId, code: form.code.trim(), name: form.name.trim() });
      setShowModal(false);
      await refresh();
      toast.success(editId ? 'Staff role updated.' : 'Staff role created.');
    } catch (e: any) {
      setFormError(e?.message ?? 'Failed to save staff role.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(r: StaffRole) {
    if (!confirm(`Delete staff role “${r.name}”? This cannot be undone.`)) return;
    setError(null);
    try {
      await api.deleteStaffRole(r.id);
      await refresh();
      toast.success('Staff role deleted.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to delete staff role.';
      setError(msg);
      toast.error(msg);
    }
  }

  return (
    <OrgLayout>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>Staff Role</h1>
        <button className="primary" onClick={openCreate}>+ Add staff role</button>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <label style={{ flex: 1 }}>
          <div className="muted">Code or Name</div>
          <input value={search} placeholder="Code / Name" onChange={e => setSearch(e.target.value)} />
        </label>
      </div>

      {error && <div className="error" style={{ margin: '12px 0' }}>{error}</div>}

      <div className="card" style={{ marginTop: 16, padding: 0, overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>S.no</th>
              <th>Role Code</th>
              <th>Role Name</th>
              <th style={{ width: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!filtered && <tr><td colSpan={4} className="muted">Loading…</td></tr>}
            {filtered && filtered.length === 0 && (
              <tr><td colSpan={4} className="muted">
                {search ? 'No staff roles match your search.' : 'No staff roles yet.'}
              </td></tr>
            )}
            {filtered?.map((r, idx) => (
              <tr key={r.id}>
                <td>{idx + 1}</td>
                <td className="slug">{r.code}</td>
                <td style={{ fontWeight: 600 }}>{r.name}</td>
                <td>
                  <button className="link-btn" onClick={() => openEdit(r)}>Edit</button>
                  <button className="link-btn" style={{ color: 'var(--danger)' }} onClick={() => remove(r)}>Delete</button>
                  <RecordInfoButton entity="staff-roles" id={r.id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal title={editId ? 'Edit staff role' : 'Create staff role'} onClose={() => setShowModal(false)}>
          <form onSubmit={submit} className="col" style={{ marginTop: 16 }}>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: '0 0 200px' }}>
                <div className="muted">Role code *</div>
                <input value={form.code} required maxLength={64} autoFocus
                       onChange={e => setForm({ ...form, code: e.target.value })} placeholder="HOD" />
              </label>
              <label style={{ flex: 1 }}>
                <div className="muted">Role name *</div>
                <input value={form.name} required maxLength={200}
                       onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Head of Department" />
              </label>
            </div>
            {formError && <div className="error">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={busy}>
                {busy ? 'Saving…' : editId ? 'Save changes' : 'Create staff role'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </OrgLayout>
  );
}
